<template>
  <div
    class="onboarding-text-root"
    :class="{
      'onboarding-text-root--thanks-for-input': isThanksForInput,
      'onboarding-text-root--personalize': page.id === 'personalize',
    }"
  >
    <Logo
      v-if="isThanksForInput"
      class="onboarding-text-root__logo"
      :withText="true"
    />

    <Layout
      :id="page.id"
      class="onboarding-text"
      :class="{
        'onboarding-text--text-large': page.textSize === 'large',
        'onboarding-text--text-left': page.textAlignment === 'left',
        'onboarding-text--thanks-for-input': isThanksForInput,
      }"
      :title="page.title"
      :subtitle="page.subtitle"
    >
      <template #aboveTitle>
        <div class="onboarding-text__image-wrapper">
          <img
            v-if="page.image"
            class="onboarding-text__image"
            :class="imageAnimationClass"
            :src="image"
            :width="page.image.width"
            :height="page.image.height"
            alt=""
          />
          <Rive
            v-else-if="page.animation"
            class="onboarding-text__animation"
            :src="animation!"
            :width="page.animation.width"
            :height="page.animation.height"
            :stateMachines="page.animation.stateMachines"
          />
        </div>
      </template>
    </Layout>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { QuestionnaireView, getImage } from '@/models/onboarding'
import type { TextPage } from '@/models/onboarding.types'

import Logo from '@/components/ui/Logo.vue'
import Layout from '../Layout.vue'
import Rive from '@/app/components/Rive.vue'

const props = defineProps<{
  view: QuestionnaireView
}>()

const page = computed(() => {
  return props.view.currentPage as TextPage
})

const isThanksForInput = computed(() => page.value.id === 'thanks-for-input')

const image = computed(() => {
  if (!page.value.image) {
    return
  }
  return getImage(page.value.image.src)
})

const animation = computed(() => {
  if (!page.value.animation) {
    return
  }
  return getImage(page.value.animation.src)
})

const imageAnimationClass = computed(() => {
  if (!page.value.imageAnimation) {
    return ''
  }
  return `onboarding-text__image--${page.value.imageAnimation}`
})
</script>

<style lang="scss" scoped>
.onboarding-text {
  justify-content: center;

  :deep(.onboarding-layout__above-title) {
    min-height: 130px;
    max-height: 100%;
    margin-bottom: 35px;
    @media (min-width: $w-medium-b) {
      font-size: 24px;
      line-height: 30px;
    }
  }

  &__image-wrapper {
    width: 100%;
    height: 100%;
  }

  &__image,
  &__animation,
  &__animation {
    display: block;
    width: 100%;
    max-height: 100%;
    margin-inline: auto;
  }

  &__image,
  &__animation--wave {
    animation: wave 1.8s 0.3s infinite;
    transform-origin: 20% 100%;
  }

  &__image,
  &__animation--slide-up {
    animation: slide-up 0.5s forwards ease-out;
  }

  :deep(.onboarding-layout__subtitle) {
    font-size: 18px;
    line-height: 24px;
  }
}

.onboarding-text--text-large {
  :deep(.onboarding-layout__title) {
    font-size: 32px;
    line-height: 36px;
  }
}

.onboarding-text--text-left {
  :deep(.onboarding-layout__title) {
    text-align: left;
  }

  :deep(.onboarding-layout__subtitle) {
    text-align: left;
  }

  :deep(.onboarding-layout__above-title) {
    max-width: var(--onboarding-outer-width);
  }

  .onboarding-text {
    &__image,
    &__animation {
      margin-inline: unset;
    }
  }
}

#intro.onboarding-text {
  .onboarding-text__image {
    width: 59px;
  }

  :deep(.onboarding-layout__above-title) {
    min-height: 0;
    margin-bottom: 24px;
  }

  :deep(.onboarding-layout__above-title),
  :deep(.onboarding-layout__text) {
    max-width: var(--onboarding-inner-width);
  }
}

#great-job.onboarding-text {
  .onboarding-text__image {
    width: 257px;
  }
}

.onboarding-text-root {
  display: contents;

  &--thanks-for-input,
  &--personalize {
    display: block;
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
  }

  &__logo {
    position: absolute;
    top: max(22px, env(safe-area-inset-top, 0px));
    left: 50%;
    z-index: 2;
    height: 24px;
    transform: translateX(-50%);
    pointer-events: none;
  }
}

.onboarding-text--thanks-for-input {
  position: relative;

  &.onboarding-text--text-large {
    padding-top: 0;
  }

  :deep(.onboarding-layout__text) {
    position: relative;
    z-index: 1;
    margin-bottom: 80px;

    @media (min-width: $w-medium-b) {
      margin-bottom: 120px;
    }
  }

  :deep(.onboarding-layout__above-title > *) {
    max-width: none !important;
  }

  :deep(.onboarding-layout__above-title) {
    position: relative;
    z-index: 0;
    margin-inline: calc(-1 * var(--onboarding-padding));
    width: calc(100% + 2 * var(--onboarding-padding));
  }

  .onboarding-text__image-wrapper {
    width: 100%;
    aspect-ratio: 375 / 770;
  }

  .onboarding-text__image {
    position: absolute;
    top: 0;
    z-index: -1;
  }
}

#people-grow {
  :deep(.onboarding-layout__title),
  :deep(.onboarding-layout__subtitle) {
    @media only screen and (min-width: $w-mobile-b) {
      text-align: center;
    }
  }
}

@keyframes slide-up {
  0% {
    transform: translateY(16px);
  }
  100% {
    transform: translateY(0);
  }
}

@keyframes wave {
  0% {
    rotate: 0deg;
  }
  6% {
    rotate: -4deg;
  }
  12%,
  15% {
    rotate: 15deg;
  }
  21% {
    rotate: -12deg;
  }
  27%,
  30% {
    rotate: 15deg;
  }
  36% {
    rotate: -8deg;
  }
  42%,
  45% {
    rotate: 15deg;
  }
  51% {
    rotate: -4deg;
  }
  57%,
  60% {
    rotate: 5deg;
  }
  66% {
    rotate: 0deg;
  }
  100% {
    rotate: 0deg;
  }
}
</style>
